import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Check, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";
import { Toaster } from "../ui/toaster";
import { PRICING } from "../../mock";
import { useAuth } from "../../context/AuthContext";
import Reveal from "./Reveal";
import { subscribeInr, buyTeamPassUsd, watchSubscriptionStatus } from "../../lib/teamCheckout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Pricing = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [live, setLive] = useState(null);
  useEffect(() => {
    axios.get(`${API}/pricing`).then((r) => setLive(r.data)).catch(() => {});
    return watchSubscriptionStatus();
  }, []);
  const team = live?.plans?.find((p) => p.id === "team" && p.available);
  const payRzp = !!live?.payments?.razorpay;
  const payStripe = !!live?.payments?.stripe;
  const isPaid = (p) => p.name !== "Free" && p.name !== "Enterprise";
  const cards = PRICING.map((p) => {
    if (p.planId) {  // Starter / Pro (INR subscriptions, Gemini 2.5 Flash only)
      const lp = live?.plans?.find((x) => x.id === p.planId && x.available);
      if (!lp) return { ...p, price: "…", features: ["Loading the current price"] };
      return {
        ...p,
        price: `₹${Number(lp.inr).toLocaleString("en-IN")}`,
        inr: live.price_note,
        features: [`${Number(lp.runs_per_30_days).toLocaleString("en-IN")} agent runs per 30 days`,
          `Runs use ${lp.run_model?.name}; every run counts as 1`,
          "Fixed AI budget per run; a run too large for it is refused and doesn't count",
          "UPI Autopay or card via Razorpay", ...(lp.coupons === false ? ["No coupons"] : [])],
      };
    }
    if (p.name !== "Team") return p;
    if (!team) return { ...p, price: "…", features: ["Loading the current price"] };
    return {
      ...p,
      price: `₹${Number(team.inr).toLocaleString("en-IN")}`,
      inr: `${live.price_note} · outside India US$${Number(team.usd_pass?.usd ?? team.usd).toFixed(2)} for a 30-day pass (no renewal)`,
      features: [`${Number(team.runs_per_30_days).toLocaleString("en-IN")} agent runs per 30 days`,
        `Runs use ${team.run_model?.name}; every run counts as 1`, "UPI Autopay or card via Razorpay",
        "Reminder before each renewal; cancel in Settings", "Everything in Free"],
    };
  });

  const handleCta = async (plan) => {
    if (plan.name === "Free") {
      navigate("/workspace");
      return;
    }
    if (plan.name === "Enterprise") {
      const email = process.env.REACT_APP_CONTACT_EMAIL;
      window.location.href = email ? `mailto:${email}?subject=Nimbus%20Enterprise` : "https://headkeysoftwaresolution.com";
      return;
    }
    if (!user) {
      toast({ title: "Sign in first", description: `${plan.name} is added to your account.` });
      navigate("/login");
      return;
    }
    // Starter / Pro / Team in India -> Razorpay subscription (₹, renews monthly)
    const route = { Starter: "starter", Pro: "pro", Team: "team" }[plan.name] || "team";
    try {
      setLoadingPlan(plan.name);
      await subscribeInr({ plan: route, user, onDone: () => navigate("/settings") });
      setLoadingPlan(null);
    } catch (e) {
      setLoadingPlan(null);
      const detail = e?.response?.data?.detail;
      toast({ title: "Checkout unavailable", description: typeof detail === "string" ? detail : "Please try again in a moment." });
    }
  };

  const buyUsd = async () => {
    if (!user) { navigate("/login"); return; }
    try {
      setLoadingPlan("TeamUSD");
      await buyTeamPassUsd();
    } catch (e) {
      setLoadingPlan(null);
      const detail = e?.response?.data?.detail;
      toast({ title: "Checkout unavailable", description: typeof detail === "string" ? detail : "Please try again in a moment." });
    }
  };

  return (
    <section id="pricing" className="relative py-24 sm:py-28">
      <Toaster />
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-wider text-primary">Pricing</span>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              Simple plans that scale with your workflows
            </h2>
            <p className="mt-3 text-sm text-muted-foreground">
              Prices in Indian rupees are inclusive of GST. <a href="/pricing" className="underline hover:text-primary">See all plans</a>
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid items-start gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {cards.map((p, i) => (
            <Reveal key={p.name} delay={i * 90}>
              <div
                className={`relative flex h-full flex-col rounded-2xl border p-7 transition-all duration-300 ${
                  p.highlight
                    ? "border-primary bg-card shadow-xl shadow-primary/10 lg:-translate-y-3"
                    : "border-border bg-card hover:-translate-y-1 hover:shadow-lg"
                }`}
              >
                {p.highlight && (
                  <span className="absolute -top-3 left-7 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground shadow">
                    Most popular
                  </span>
                )}
                <h3 className="font-display text-lg font-semibold text-foreground">{p.name}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{p.blurb}</p>
                <div className="mt-5 flex items-baseline gap-1">
                  <span className="font-display text-4xl font-bold text-foreground">{p.price}</span>
                  <span className="text-sm text-muted-foreground">{p.period}</span>
                </div>
                {p.inr && <p className="mt-1 text-sm text-muted-foreground" data-testid={`pricing-inr-${p.name.toLowerCase()}`}>{p.inr}</p>}
                {isPaid(p) && live && !payRzp ? (
                  <p className="mt-6 text-sm text-muted-foreground" data-testid={`pricing-unavailable-${p.name.toLowerCase()}`}>Payments are temporarily unavailable</p>
                ) : (
                <Button
                  className={`mt-6 w-full rounded-full font-semibold ${
                    p.highlight ? "bg-primary" : "bg-secondary text-secondary-foreground hover:bg-accent"
                  }`}
                  disabled={loadingPlan === p.name}
                  onClick={() => handleCta(p)}
                >
                  {loadingPlan === p.name ? (
                    <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Redirecting…</>
                  ) : (
                    p.cta
                  )}
                </Button>
                )}
                {p.name === "Team" && team && payStripe && (
                  <button type="button" onClick={buyUsd} disabled={loadingPlan === "TeamUSD"}
                    className="mt-2 text-xs text-muted-foreground underline hover:text-primary" data-testid="pricing-team-usd">
                    Outside India? Pay US${Number(team.usd_pass?.usd ?? team.usd).toFixed(2)} by card for 30 days
                  </button>
                )}
                <ul className="mt-7 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-foreground">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
